import type { DemoAction } from './actions.ts';
import type { DemoSnapshot } from './types.ts';
import { fetchHttpDemoSnapshot, HttpDemoApiError, runHttpDemoAction } from './httpApi.ts';
import { fetchDemoSnapshot as fetchLocalDemoSnapshot, getCachedDemoSnapshotSync, getDemoSnapshotSync, replaceLocalDemoSnapshot, runLocalDemoAction } from './localApi.ts';
import { createInitialState, snapshotFromState } from './state.ts';

export type DemoDataMode = 'auto' | 'local' | 'http';

let resolvedAutoMode: Exclude<DemoDataMode, 'auto'> | null = null;

function configuredMode(): DemoDataMode {
  const raw = String(import.meta.env.VITE_DEMO_DATA_MODE || 'auto').trim().toLowerCase();
  if (raw === 'http') return 'http';
  if (raw === 'local') return 'local';
  return 'auto';
}

function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}

function isNetworkFallbackError(error: unknown): boolean {
  return error instanceof HttpDemoApiError && error.kind === 'network';
}

async function mirrorHttpSnapshot<T extends DemoSnapshot>(snapshotPromise: Promise<T>): Promise<T> {
  const snapshot = await snapshotPromise;
  replaceLocalDemoSnapshot(snapshot);
  return snapshot;
}

export function getInitialDemoSnapshot(): DemoSnapshot {
  const cachedSnapshot = getCachedDemoSnapshotSync();
  if (configuredMode() === 'http') {
    return snapshotFromState(createInitialState());
  }
  return cachedSnapshot ?? getDemoSnapshotSync();
}

export function getConfiguredDemoDataMode(): DemoDataMode {
  return configuredMode();
}

export function getResolvedDemoDataMode(): Exclude<DemoDataMode, 'auto'> | null {
  return resolvedAutoMode;
}

export async function fetchPreferredDemoSnapshot(signal?: AbortSignal): Promise<DemoSnapshot> {
  const mode = configuredMode();
  if (mode === 'local') {
    resolvedAutoMode = 'local';
    return fetchLocalDemoSnapshot();
  }

  if (mode === 'http') {
    resolvedAutoMode = 'http';
    return mirrorHttpSnapshot(fetchHttpDemoSnapshot(signal));
  }

  try {
    const snapshot = await fetchHttpDemoSnapshot(signal);
    if (resolvedAutoMode === 'local') {
      return getCachedDemoSnapshotSync() ?? fetchLocalDemoSnapshot();
    }
    resolvedAutoMode = 'http';
    return mirrorHttpSnapshot(Promise.resolve(snapshot));
  } catch (error) {
    if (isAbortError(error)) throw error;
    if (!isNetworkFallbackError(error)) throw error;
    resolvedAutoMode = 'local';
    return getCachedDemoSnapshotSync() ?? fetchLocalDemoSnapshot();
  }
}

export async function runPreferredDemoAction(action: DemoAction): Promise<DemoSnapshot> {
  const mode = configuredMode();
  if (mode === 'local') {
    resolvedAutoMode = 'local';
    return runLocalDemoAction(action);
  }

  if (mode === 'http') {
    resolvedAutoMode = 'http';
    return mirrorHttpSnapshot(runHttpDemoAction(action));
  }

  if (resolvedAutoMode === 'local') {
    return runLocalDemoAction(action);
  }

  try {
    const snapshot = await mirrorHttpSnapshot(runHttpDemoAction(action));
    resolvedAutoMode = 'http';
    return snapshot;
  } catch (error) {
    if (!isNetworkFallbackError(error)) throw error;
    resolvedAutoMode = 'local';
    return runLocalDemoAction(action);
  }
}
